import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import './DatePicker.scss';
import { CalendarBlank, Star } from '@phosphor-icons/react';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  onClear?: () => void;
  usePortal?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, onClear, usePortal = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [alignRight, setAlignRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && usePortal && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen, usePortal]);

  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setAlignRight(rect.right > window.innerWidth);
    }
  }, [isOpen]);

  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;
    // Parse as local date (YYYY-MM-DD format)
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  const handleSelectDate = (date: Date) => {
    onChange(formatDate(date));
    setIsOpen(false);
  };

  const handleToday = () => {
    handleSelectDate(getToday());
  };

  const handleThisEvening = () => {
    // For "This Evening", we still use today's date
    handleSelectDate(getToday());
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const today = getToday();
    const selectedDate = parseDate(value);
    
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Week day headers
    const headers = weekDays.map(day => (
      <div key={day} className="tasksDatePickerWeekDay">{day}</div>
    ));
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="tasksDatePickerDay tasksDatePickerDayEmpty" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      date.setHours(0, 0, 0, 0);
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
      
      days.push(
        <button
          key={day}
          type="button"
          className={`tasksDatePickerDay ${isToday ? 'tasksDatePickerDayToday' : ''} ${isSelected ? 'tasksDatePickerDaySelected' : ''}`}
          onClick={() => handleSelectDate(date)}
        >
          {day}
        </button>
      );
    }
    
    return (
      <div className="tasksDatePickerCalendar">
        <div className="tasksDatePickerGrid">
          {headers}
          {days}
        </div>
      </div>
    );
  };

  const nextWeek = () => {
    const days = [];
    const today = getToday();
    const selectedDate = parseDate(value);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNum = date.getDate();
      const isToday = i === 0;
      const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
      
      days.push(
        <button
          key={i}
          type="button"
          className={`tasksDatePickerWeekButton ${isSelected ? 'tasksDatePickerWeekButtonSelected' : ''}`}
          onClick={() => handleSelectDate(date)}
        >
          <div className="tasksDatePickerWeekButtonDay">{dayName}</div>
          <div className="tasksDatePickerWeekButtonNum">{dayNum}</div>
        </button>
      );
    }
    
    return days;
  };

  const formatDisplayDate = () => {
    if (!value) return 'When';
    
    const date = parseDate(value);
    if (!date) return 'When';
    
    const today = getToday();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const renderDropdown = () => (
    <div 
      ref={dropdownRef}
      className={`tasksDatePickerDropdown ${alignRight ? 'tasksDatePickerDropdownAlignRight' : ''}`}
      style={usePortal ? {
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        zIndex: 10000
      } : undefined}
    >
      {/* Quick actions */}
      <div className="tasksDatePickerQuick">
        <button
          type="button"
          className="tasksDatePickerQuickButton"
          onClick={handleToday}
        >
          <Star size={16} weight="fill" color="#fbbf24" />
          <span>Today</span>
        </button>
        <button
          type="button"
          className="tasksDatePickerQuickButton"
          onClick={handleThisEvening}
        >
          <span className="tasksDatePickerMoon">🌙</span>
          <span>This Evening</span>
        </button>
      </div>

      {/* Next 7 days */}
      <div className="tasksDatePickerWeek">
        {nextWeek()}
      </div>

      {/* Month calendar */}
      <div className="tasksDatePickerMonthHeader">
        <button
          type="button"
          className="tasksDatePickerMonthNav"
          onClick={() => {
            const newMonth = new Date(currentMonth);
            newMonth.setMonth(newMonth.getMonth() - 1);
            setCurrentMonth(newMonth);
          }}
        >
          ‹
        </button>
        <div className="tasksDatePickerMonthTitle">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          className="tasksDatePickerMonthNav"
          onClick={() => {
            const newMonth = new Date(currentMonth);
            newMonth.setMonth(newMonth.getMonth() + 1);
            setCurrentMonth(newMonth);
          }}
        >
          ›
        </button>
      </div>

      {renderCalendar()}

      {/* Someday option */}
      <div className="tasksDatePickerFooter">
        <button
          type="button"
          className="tasksDatePickerSomedayButton"
          onClick={() => {
            if (onClear) onClear();
            setIsOpen(false);
          }}
        >
          <span>📦</span>
          <span>Someday</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="tasksDatePicker" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`tasksFieldButton tasksDatePickerButton ${value ? 'tasksDatePickerButtonActive' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarBlank size={14} weight="bold" />
        <span>{formatDisplayDate()}</span>
      </button>

      {isOpen && (
        usePortal 
          ? ReactDOM.createPortal(renderDropdown(), document.body)
          : renderDropdown()
      )}
    </div>
  );
};

export default DatePicker;


